/**
 * OIDC 로그아웃 관련 유틸리티 함수들
 * Spring Authorization Server 표준 OIDC RP-Initiated Logout 구현
 */

import { getValidIdToken } from './token-validation-utils';
import { I18N_LANG_KEY } from '@edusense/shared-constants';

/**
 * OIDC 로그아웃 상태 키 (microblog-lms 호환)
 */
const LOGOUT_STATE_KEY = 'oidc_logout_state';

/**
 * 랜덤 state 생성 (OIDC 표준, microblog-lms 참조)
 */
const generateRandomState = (): string => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

/**
 * OIDC 로그아웃 파라미터 인터페이스
 */
interface OidcLogoutParams {
  authServerUrl: string;
  clientId: string;
  postLogoutRedirectUri: string;
}

/**
 * 환경 변수에서 OIDC 로그아웃 파라미터 추출
 */
const getOidcLogoutParams = (authConfig?: any): OidcLogoutParams => {
  const AUTH_SERVER_URL = authConfig?.api?.authServerUrl || import.meta.env.VITE_AUTH_SERVER_URL || 'https://auth.ugot.uk';
  const APP_URL = import.meta.env.VITE_OAUTH2_REDIRECT_URI?.replace('/oauth2/redirect', '') || 'https://app.ugot.uk';
  const clientId = authConfig?.oauth2?.clientId || import.meta.env.VITE_OAUTH2_CLIENT_ID || 'app-client';
  
  const configuredLogoutUri = authConfig?.oauth2?.logoutRedirectUri;
  
  const postLogoutRedirectUri = configuredLogoutUri || `${APP_URL}/landing?logout=true`;

  return {
    authServerUrl: AUTH_SERVER_URL,
    clientId,
    postLogoutRedirectUri
  };
};

/**
 * OIDC 로그아웃 URL 생성.
 * authserver의 /connect/logout 필터는 client_id와 post_logout_redirect_uri만으로도
 * 서버 세션을 정리할 수 있으므로 id_token_hint가 없을 때도 로그아웃을 시도한다.
 * 
 * @returns 생성된 로그아웃 URL과 state 값
 */
export const createOidcLogoutUrl = (authConfig?: any, idTokenHint?: string | null): { url: string; state: string } => {
  console.log('[AuthUtils] 🏗️ createOidcLogoutUrl 시작');
  
  // ID 토큰 검증
  console.log('[AuthUtils] 🔍 getValidIdToken 호출');
  const idToken = idTokenHint || getValidIdToken();
  console.log('[AuthUtils] 🔍 getValidIdToken 결과:', !!idToken);
  
  console.log('[AuthUtils] ⚙️ getOidcLogoutParams 호출');
  const params = getOidcLogoutParams(authConfig);
  console.log('[AuthUtils] ⚙️ 로그아웃 파라미터 생성 완료');
  
  // OIDC 표준 state 파라미터 생성 (microblog-lms 참조)
  console.log('[AuthUtils] 🎲 generateRandomState 호출');
  const state = generateRandomState();
  console.log('[AuthUtils] 🎲 state 생성 완료:', state);
  
  console.log('[AuthUtils] 📋 OIDC 로그아웃 파라미터 상세:');
  console.log('  🌐 authServerUrl:', params.authServerUrl);
  console.log('  🔑 clientId:', params.clientId);
  console.log('  🎫 id_token_hint:', idToken ? `${idToken.substring(0, 30)}...` : 'null');
  console.log('  🔄 post_logout_redirect_uri:', params.postLogoutRedirectUri);
  console.log('  🎲 state:', state);
  
  // OIDC 로그아웃 URL 생성 (state 파라미터 포함)
  console.log('[AuthUtils] 🔨 로그아웃 URL 조립 중...');
  const logoutUrl = new URL(`${params.authServerUrl}/connect/logout`);
  if (idToken) {
    logoutUrl.searchParams.set('id_token_hint', idToken);
  }
  logoutUrl.searchParams.set('post_logout_redirect_uri', params.postLogoutRedirectUri);
  logoutUrl.searchParams.set('client_id', params.clientId);
  logoutUrl.searchParams.set('state', state);
  
  console.log('[AuthUtils] ✅ OIDC 로그아웃 URL 생성 완료');
  console.log('[AuthUtils] 🌐 완성된 로그아웃 URL:', logoutUrl.toString());
  
  return { url: logoutUrl.toString(), state };
};

/**
 * OIDC 로그아웃 실행
 * 
 * @param navigate - 실패 시 사용할 navigate 함수
 * @param authConfig - 인증 설정
 */
export const performOidcLogout = (
  navigate?: (path: string) => void,
  authConfig?: any,
  idTokenHint?: string | null,
): void => {
  console.log('[AuthUtils] 🚀 performOidcLogout 시작');
  
  try {
    console.log('[AuthUtils] 🔗 createOidcLogoutUrl 호출 시작');
    const { url: logoutUrl, state } = createOidcLogoutUrl(authConfig, idTokenHint);
    console.log('[AuthUtils] 🔗 createOidcLogoutUrl 호출 성공');
    console.log('[AuthUtils] 🌐 생성된 로그아웃 URL:', logoutUrl);
    console.log('[AuthUtils] 🔑 생성된 state:', state);
    
    // OIDC 표준 state 저장 (microblog-lms 참조)
    localStorage.setItem(LOGOUT_STATE_KEY, state);
    console.log('[AuthUtils] 💾 OIDC 로그아웃 state 저장 완료');
    
    // 현재 페이지 URL 확인
    console.log('[AuthUtils] 📍 현재 페이지 URL:', window.location.href);
    
    // 약간의 딜레이 후 리다이렉트 (상태 변경 완료 보장)
    console.log('[AuthUtils] ⏰ 100ms 딜레이 후 리다이렉트 예정');
    setTimeout(() => {
      console.log('[AuthUtils] 🚀 OIDC 로그아웃 리다이렉트 실행 시작');
      console.log('[AuthUtils] 🎯 리다이렉트 대상 URL:', logoutUrl);
      
      // 리다이렉트 실행
      window.location.href = logoutUrl;
      
      console.log('[AuthUtils] 🎯 window.location.href 설정 완료');
      // 참고: 이 로그 이후에는 페이지가 리다이렉트되므로 더 이상 로그가 보이지 않을 수 있음
    }, 100);
    
  } catch (error) {
    console.error('[AuthUtils] ❌ OIDC 로그아웃 실행 실패:', error);
    
    if (navigate) {
      // OIDC 실패 시 직접 랜딩 페이지로 이동
      const errorMessage = error instanceof Error ? error.message : 'oidc_failed';
      console.log('[AuthUtils] 🔄 에러 발생으로 navigate 함수로 이동:', `/landing?logout=true&error=${encodeURIComponent(errorMessage)}`);
      navigate(`/landing?logout=true&error=${encodeURIComponent(errorMessage)}`);
    } else {
      console.log('[AuthUtils] ⚠️ navigate 함수가 없어 에러를 다시 던짐');
      throw error; // navigate가 없으면 에러를 다시 던짐
    }
  }
};

/**
 * OIDC 로그아웃 state 검증 (미래 확장용)
 * 
 * @param receivedState - 서버에서 받은 state 값
 * @returns state가 유효한지 여부
 */
export const validateLogoutState = (receivedState: string): boolean => {
  const storedState = localStorage.getItem(LOGOUT_STATE_KEY);
  const isValid = storedState === receivedState;
  
  if (isValid) {
    console.log('[AuthUtils] OIDC 로그아웃 state 검증 성공');
    localStorage.removeItem(LOGOUT_STATE_KEY);
  } else {
    console.warn('[AuthUtils] OIDC 로그아웃 state 검증 실패:', { storedState, receivedState });
  }
  
  return isValid;
};
